import { useState } from "react";
import { toast } from "sonner";
import type { Order, OrderItem } from "../../services/ordersApi";
import { useAvailableGreenhouseStock, useReserveGreenhouseStock } from "../../hooks/useGreenhouseStock";
import type { AvailableGreenhouseStockDto } from "../../services/sowingInstructionsApi";
import { splitStockByCategory } from "../../utils/stockCategory";
import { availableInOrderUnits, isPerSeedlingPricing, stockMatchesOrder } from "../../utils/orderStockAvailability";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldLabel } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PackageCheck, Warehouse, Leaf } from "lucide-react";

type StockCategoryTab = "vegetable" | "agroforestry";

interface AssignStockDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AssignStockDialog = ({
  order,
  open,
  onOpenChange,
}: AssignStockDialogProps) => {
  // Only company-seedling items that aren't fully fulfilled can have stock assigned
  const assignableItems = order.orderItems.filter(
    (item) => item.isCompanySeedling && !item.isFullyFulfilled
  );

  const [selectedItemId, setSelectedItemId] = useState<string>(
    assignableItems[0]?.id ?? ""
  );
  const selectedItem = assignableItems.find((i) => i.id === selectedItemId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5" />
            Assign Stock to Order
          </DialogTitle>
          <DialogDescription>
            Order <strong>{order.orderNumber}</strong> — {order.customerName}.
            Select available stock entries to assign against unfulfilled items.
          </DialogDescription>
        </DialogHeader>

        {assignableItems.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No assignable items — all items are either fulfilled or customer-seed items.
          </p>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            {/* Item selector (if multiple) */}
            {assignableItems.length > 1 && (
              <div className="space-y-1">
                <p className="text-sm font-medium">Order Item</p>
                <div className="flex flex-wrap gap-2">
                  {assignableItems.map((item) => (
                    <Button
                      key={item.id}
                      type="button"
                      variant="outline"
                      onClick={() => setSelectedItemId(item.id)}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                        selectedItemId === item.id
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-muted hover:bg-muted"
                      }`}
                    >
                      {item.productName}
                      {item.variety ? ` — ${item.variety}` : ""}
                      <span className="ml-1 text-muted-foreground">
                        ({item.quantity - item.quantityFulfilled} pending)
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Stock picker for selected item */}
            {selectedItem && (
              <StockPickerForItem
                key={selectedItem.id}
                item={selectedItem}
                orderId={order.id}
                enabled={open}
                onSuccess={() => {
                  toast.success("Stock assigned successfully");
                }}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Per-item stock picker ─────────────────────────────────────────────────

interface StockPickerProps {
  item: OrderItem;
  orderId: string;
  enabled: boolean;
  onSuccess: () => void;
}

const StockPickerForItem = ({ item, orderId, enabled, onSuccess }: StockPickerProps) => {
  const [selectedEntryId, setSelectedEntryId] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(0);
  const [assignedInSession, setAssignedInSession] = useState<number>(0);
  const [stockCategoryTab, setStockCategoryTab] = useState<StockCategoryTab>("vegetable");

  const pricingMode = item.pricingMode ?? item.pricingModeName ?? (item.unit === 5 ? 2 : 1);
  const isPerSeedling = isPerSeedlingPricing(pricingMode);
  const stockUnitLabel = isPerSeedling ? "pieces" : "trays";
  const pendingQty = Math.max(0, item.quantity - item.quantityFulfilled - assignedInSession);
  const isFullyAssigned = pendingQty <= 0;

  const stockQuery = useAvailableGreenhouseStock({
    cropTypeId: item.cropTypeId || undefined,
    variety: item.variety || undefined,
    trayType: !isPerSeedling && item.trayType ? item.trayType : undefined,
    enabled: enabled && !!item.cropTypeId && !isFullyAssigned,
  });

  const reserveMutation = useReserveGreenhouseStock();

  const compatibleEntries = (stockQuery.data ?? []).filter((entry) => stockMatchesOrder(entry, {
    cropTypeId: item.cropTypeId,
    cropTypeName: item.cropType,
    variety: item.variety,
    pricingMode,
    trayType: item.trayType,
  }));
  const readyEntries = compatibleEntries.filter((e) => e.isReadyForDispatch);
  const pipelineEntries = compatibleEntries.filter((e) => !e.isReadyForDispatch);
  const readyByCategory = splitStockByCategory(readyEntries, (e) => e.isAgroforestry);
  const pipelineByCategory = splitStockByCategory(pipelineEntries, (e) => e.isAgroforestry);
  const selectedReadyEntries = stockCategoryTab === "vegetable"
    ? readyByCategory.vegetable
    : readyByCategory.agroforestry;
  const selectedPipelineEntries = stockCategoryTab === "vegetable"
    ? pipelineByCategory.vegetable
    : pipelineByCategory.agroforestry;
  const totalReadyAvailable = readyEntries.reduce((sum, e) => sum + availableInOrderUnits(e, pricingMode), 0);
  const hasEnoughStock = totalReadyAvailable >= pendingQty;

  const selectedEntry = readyEntries.find((e) => e.entryId === selectedEntryId);
  const maxQty = selectedEntry
    ? Math.min(availableInOrderUnits(selectedEntry, pricingMode), pendingQty)
    : 0;

  const handleAssign = () => {
    if (!selectedEntry || quantity <= 0 || quantity > maxQty ||
        (isPerSeedling && !Number.isSafeInteger(quantity))) return;
    reserveMutation.mutate(
      { entryId: selectedEntryId, orderId, orderItemId: item.id, quantity },
      {
        onSuccess: () => {
          setAssignedInSession((prev) => prev + quantity);
          setSelectedEntryId("");
          setQuantity(0);
          onSuccess();
        },
        onError: (err: unknown) => {
          const message =
            err instanceof Error ? err.message : "Failed to assign stock";
          toast.error(message);
        },
      }
    );
  };

  return (
    <div className="space-y-3">
      {/* Item summary header */}
      <div className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2">
        <div className="text-sm">
          <span className="font-medium">{item.productName}</span>
          {item.variety && (
            <span className="text-muted-foreground"> — {item.variety}</span>
          )}
          {item.trayTypeName && (
            <Badge variant="outline" className="ml-2 text-xs">
              {item.trayTypeName}
            </Badge>
          )}
        </div>
        <div className="text-sm text-right">
          {isFullyAssigned ? (
            <span className="text-green-700 font-medium">✓ Fully assigned</span>
          ) : (
            <>
              <span className="text-muted-foreground">Pending: </span>
              <span className="font-medium">
                {pendingQty} {stockUnitLabel}
              </span>
              {assignedInSession > 0 && (
                <span className="text-green-700 text-xs ml-1">
                  ({assignedInSession} assigned)
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stock entries list — hidden when fully assigned */}
      {!stockQuery.isLoading && !isFullyAssigned && !hasEnoughStock && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1">
          <p className="text-sm font-medium text-amber-800">Insufficient ready stock</p>
          <p className="text-xs text-amber-700">
            This item needs <strong>{pendingQty} {stockUnitLabel}</strong> but only{" "}
            <strong>{totalReadyAvailable} {stockUnitLabel}</strong> are ready for dispatch.
            {pipelineEntries.length > 0 && <span> Some batches are still in the pipeline.</span>}
            {" "}A new sowing instruction may be needed for the shortfall.
          </p>
        </div>
      )}
      {isFullyAssigned ? (
        <p className="text-sm text-green-700 py-2">
          All {item.quantity} {stockUnitLabel} have been assigned for this item.
        </p>
      ) : (
      <div className="max-h-[340px] overflow-y-auto">
        {stockQuery.isLoading ? (
          <p className="text-xs text-muted-foreground p-2">Loading available stock…</p>
        ) : !compatibleEntries.length ? (
          <p className="text-xs text-amber-600 p-2">
            No available stock for this crop/variety. A sowing instruction may be needed.
          </p>
        ) : (
          <div className="space-y-3 pr-2">
            <Tabs value={stockCategoryTab} onValueChange={(v) => setStockCategoryTab(v as StockCategoryTab)}>
              <TabsList>
                <TabsTrigger value="vegetable">
                  Vegetable Seedlings ({readyByCategory.vegetable.length + pipelineByCategory.vegetable.length})
                </TabsTrigger>
                <TabsTrigger value="agroforestry">
                  Agroforestry Seedlings ({readyByCategory.agroforestry.length + pipelineByCategory.agroforestry.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Ready stock */}
            {selectedReadyEntries.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-green-700 flex items-center gap-1">
                  <Warehouse className="h-3 w-3" />
                  Ready for dispatch ({selectedReadyEntries.length})
                </p>
                {selectedReadyEntries.map((entry) => (
                  <StockEntryRow
                    key={entry.entryId}
                    entry={entry}
                    selected={selectedEntryId === entry.entryId}
                    onClick={() => {
                      setSelectedEntryId(entry.entryId);
                      setQuantity(
                        Math.min(availableInOrderUnits(entry, pricingMode), pendingQty)
                      );
                    }}
                    stockUnitLabel={stockUnitLabel}
                    availableQuantity={availableInOrderUnits(entry, pricingMode)}
                  />
                ))}
              </div>
            )}

            {/* Pipeline stock (display only) */}
            {selectedPipelineEntries.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
                  <Leaf className="h-3 w-3" />
                  In pipeline — not yet ready ({selectedPipelineEntries.length})
                </p>
                {selectedPipelineEntries.map((entry) => (
                  <div
                    key={entry.entryId}
                    className="flex items-center gap-2 p-2 rounded text-xs text-muted-foreground"
                  >
                    <div className="w-3 h-3 rounded-full border-2 border-amber-400 flex-shrink-0" />
                    <div>
                      <span>
                        {availableInOrderUnits(entry, pricingMode)} {stockUnitLabel}
                      </span>
                      {entry.source === "Agroforestry" && (
                        <span className="ml-1 text-teal-600">(Agroforestry · healing)</span>
                      )}
                      {entry.greenhouseName && (
                        <span>
                          {" "}· {entry.greenhouseName}
                          {entry.bayName ? ` / ${entry.bayName}` : ""}
                          {entry.chamberName ? ` / ${entry.chamberName}` : ""}
                        </span>
                      )}
                      {entry.expectedReadyDate && (
                        <span className="text-amber-600">
                          {" "}· expected ready{" "}
                          {new Date(entry.expectedReadyDate).toLocaleDateString()}
                        </span>
                      )}
                      <span> · entered {new Date(entry.entryDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedReadyEntries.length === 0 && selectedPipelineEntries.length === 0 && (
              <p className="text-xs text-muted-foreground p-2">No stock entries available in this tab.</p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Quantity input + confirm */}
      {selectedEntryId && selectedEntry && !isFullyAssigned && (
        <div className="border-t pt-3 space-y-3">
          <div className="flex items-center gap-3">
            <FieldLabel htmlFor={`assign-stock-quantity-${item.id}`} className="text-sm font-medium whitespace-nowrap">
              Quantity to assign:
            </FieldLabel>
            <Input
              id={`assign-stock-quantity-${item.id}`}
              type="number"
              min={isPerSeedling ? 1 : 0}
              step={isPerSeedling ? 1 : "any"}
              max={maxQty}
              value={quantity}
              onChange={(e) =>
                setQuantity(Math.min(Number(e.target.value), maxQty))
              }
              className="w-24 px-2 py-1 text-sm border rounded-md"
            />
            <span className="text-xs text-muted-foreground">
              / {maxQty} {stockUnitLabel} max
            </span>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-green-700">
              ✓ Selected: {availableInOrderUnits(selectedEntry, pricingMode)} {stockUnitLabel} from{" "}
              {selectedEntry.greenhouseName || selectedEntry.source}
              {selectedEntry.bayName ? ` / ${selectedEntry.bayName}` : ""}
            </p>
            <Button
              size="sm"
              onClick={handleAssign}
              disabled={
                quantity <= 0 || quantity > maxQty || (isPerSeedling && !Number.isSafeInteger(quantity)) || reserveMutation.isPending
              }
            >
              {reserveMutation.isPending ? "Assigning…" : "Assign Stock"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Stock entry row ───────────────────────────────────────────────────────

interface StockEntryRowProps {
  entry: AvailableGreenhouseStockDto;
  selected: boolean;
  onClick: () => void;
  stockUnitLabel: string;
  availableQuantity: number;
}

const StockEntryRow = ({ entry, selected, onClick, stockUnitLabel, availableQuantity }: StockEntryRowProps) => (
  <div
    className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-xs transition-colors ${
      selected
        ? "border-primary bg-primary/5"
        : "border-transparent hover:bg-muted"
    }`}
    onClick={onClick}
  >
    <div
      className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
        selected ? "bg-primary border-primary" : "border-muted-foreground"
      }`}
    />
    <div className="flex-1">
      <span className="font-medium">
        {availableQuantity} {stockUnitLabel} available
      </span>
      {entry.source === "Agroforestry" && (
        <span className="ml-1 text-teal-600 font-medium">(Agroforestry)</span>
      )}
      {entry.greenhouseName && (
        <span className="text-muted-foreground">
          {" "}· {entry.greenhouseName}
          {entry.bayName ? ` / ${entry.bayName}` : ""}
          {entry.bedNumber ? ` / ${entry.bedNumber}` : ""}
          {entry.chamberName ? ` / ${entry.chamberName}` : ""}
        </span>
      )}
      {entry.trayType && (
        <span className="text-muted-foreground"> · {entry.trayType}</span>
      )}
      <span className="text-muted-foreground">
        {" "}· entered {new Date(entry.entryDate).toLocaleDateString()}
      </span>
      {entry.pendingDispatchQuantity > 0 && (
        <span className="text-amber-600 ml-1">
          ({entry.pendingDispatchQuantity} {entry.stockUnit.toLowerCase()} pending dispatch)
        </span>
      )}
    </div>
  </div>
);
